<template>
    <div class="users-list-view">
        <div class="page-header">
            <h1 class="page-heading">Gestion des utilisateurs</h1>
            <button class="add-user-btn" @click="showModal('add')">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Nouvel utilisateur
            </button>
        </div>

        <!-- Filtres -->
        <div class="filters-container">
            <div class="search-bar">
                <div class="input-with-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input type="text" placeholder="Rechercher un utilisateur..." v-model="search"
                        @input="handleSearch" />
                </div>
            </div>

            <div class="filter-selects">
                <div class="filter-select">
                    <select v-model="filters.role" @change="loadUsers">
                        <option value="">Tous les rôles</option>
                        <option value="admin">Administrateur</option>
                        <option value="user">Utilisateur standard</option>
                    </select>
                </div>
                <div class="filter-select">
                    <select v-model="filters.active" @change="loadUsers">
                        <option value="">Tous les statuts</option>
                        <option value="true">Actif</option>
                        <option value="false">Inactif</option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Tableau des utilisateurs -->
        <div class="users-table-container">
            <div v-if="loading" class="loading-container">
                <div class="spinner"></div>
                <p>Chargement des utilisateurs...</p>
            </div>

            <div v-else-if="users.length === 0" class="no-users">
                <div class="empty-state-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                </div>
                <h3>Aucun utilisateur trouvé</h3>
                <p>Cliquez sur "Nouvel utilisateur" pour ajouter un utilisateur au système.</p>
                <button class="start-btn" @click="showModal('add')">
                    Ajouter un utilisateur
                </button>
            </div>

            <table v-else class="users-table">
                <thead>
                    <tr>
                        <th>Nom</th>
                        <th>Email</th>
                        <th>Rôle</th>
                        <th>Statut</th>
                        <th>Date d'ajout</th>
                        <th>Ventes</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in users" :key="user.id" :class="{ 'inactive': !user.active }">
                        <td class="user-name">
                            <div class="avatar-name">
                                <div class="avatar">{{ getInitials(user.name) }}</div>
                                <span>{{ user.name }}</span>
                            </div>
                        </td>
                        <td>{{ user.email }}</td>
                        <td>
                            <span class="role-badge" :class="{ 'admin': user.role === 'admin' }">
                                {{ user.role === 'admin' ? 'Administrateur' : 'Utilisateur' }}
                            </span>
                        </td>
                        <td>
                            <span class="status-badge" :class="{ 'active': user.active, 'inactive': !user.active }">
                                {{ user.active ? 'Actif' : 'Inactif' }}
                            </span>
                        </td>
                        <td>{{ formatDate(user.created_at) }}</td>
                        <td>
                            <span v-if="user.statistics">
                                {{ user.statistics.sales_count }} ({{ formatPrice(user.statistics.sales_total) }})
                            </span>
                            <span v-else>-</span>
                        </td>
                        <td class="actions">
                            <button @click="showModal('edit', user)" class="edit-btn">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                </svg>
                                Modifier
                            </button>
                            <button @click="showModal('status', user)" class="status-btn"
                                :class="{ 'activate': !user.active, 'deactivate': user.active }">
                                <svg v-if="user.active" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
                                    <line x1="12" y1="2" x2="12" y2="12"></line>
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round" stroke-linejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="16"></line>
                                    <line x1="8" y1="12" x2="16" y2="12"></line>
                                </svg>
                                {{ user.active ? 'Désactiver' : 'Activer' }}
                            </button>
                            <button @click="showModal('delete', user)" class="delete-btn"
                                :disabled="user.active && user.statistics && user.statistics.sales_count > 0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path
                                        d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2">
                                    </path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                                Supprimer
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Pagination -->
            <PaginationComponent
                v-if="users.length > 0"
                :current-page="currentPage"
                :per-page="perPage"
                :total="total"
                :to="paginationTo"
                :from="paginationFrom"
                @page-change="goToPage"
            />
        </div>

        <!-- Modal de formulaire utilisateur -->
        <div v-if="showUserFormModal" class="modal-backdrop" @click.self="closeModal('form')">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>{{ isEditMode ? 'Modifier un utilisateur' : 'Ajouter un utilisateur' }}</h3>
                    <button class="close-btn" @click="closeModal('form')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="saveUser">
                        <div class="form-group">
                            <label for="name">Nom complet</label>
                            <input type="text" id="name" v-model="userForm.name" required>
                        </div>
                        <div class="form-group">
                            <label for="email">Email</label>
                            <input type="email" id="email" v-model="userForm.email" required>
                        </div>
                        <div class="form-group">
                            <label for="role">Rôle</label>
                            <select id="role" v-model="userForm.role" required>
                                <option value="admin">Administrateur</option>
                                <option value="user">Utilisateur standard</option>
                            </select>
                        </div>
                        <div v-if="isEditMode" class="form-group">
                            <label for="active">Statut</label>
                            <div class="toggle-switch">
                                <input type="checkbox" id="active" v-model="userForm.active">
                                <label for="active">{{ userForm.active ? 'Actif' : 'Inactif' }}</label>
                            </div>
                        </div>
                        <div v-if="!isEditMode" class="form-group">
                            <label>Un email avec un lien pour définir le mot de passe sera envoyé à
                                l'utilisateur</label>
                        </div>
                        <div class="form-actions">
                            <button type="button" class="cancel-btn" @click="closeModal('form')">Annuler</button>
                            <button type="submit" class="save-btn" :disabled="saving">
                                <div v-if="saving" class="spinner-small"></div>
                                <span v-else>{{ isEditMode ? 'Enregistrer' : 'Ajouter' }}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Modal de confirmation -->
        <div v-if="showConfirmModal" class="modal-backdrop" @click.self="closeModal('confirm')">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>{{ confirmTitle }}</h3>
                    <button class="close-btn" @click="closeModal('confirm')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div class="modal-body">
                    <p>{{ confirmMessage }}</p>
                    <div class="form-actions">
                        <button type="button" class="cancel-btn" @click="closeModal('confirm')">Annuler</button>
                        <button type="button" :class="confirmButtonClass" @click="confirmAction" :disabled="saving">
                            <div v-if="saving" class="spinner-small"></div>
                            <span v-else>{{ confirmButtonText }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted } from 'vue'
import usersService from '@/services/usersService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'

export default defineComponent({
    name: 'UsersListView',
    components: {
        PaginationComponent
    },
    setup() {
        // Données d'état
        const users = ref([]);
        const loading = ref(true);
        const saving = ref(false);
        const search = ref('');
        const filters = reactive({
            role: '',
            active: ''
        });

        // Pagination
        const currentPage = ref(1);
        const lastPage = ref(1);
        const perPage = ref(10);
        const total = ref(0);
        const paginationFrom = ref(0);
        const paginationTo = ref(0);
        const waitSearch = ref(null);
        // Modals
        const showUserFormModal = ref(false);
        const showConfirmModal = ref(false);
        const isEditMode = ref(false);
        const currentUser = ref(null);
        const userForm = reactive({
            id: null,
            name: '',
            email: '',
            role: 'user',
            active: true
        });
        const confirmAction = ref(null);
        const confirmTitle = ref('');
        const confirmMessage = ref('');
        const confirmButtonText = ref('');
        const confirmButtonClass = ref('');


        // Méthodes
        const loadUsers = async () => {
            try {
                loading.value = true;

                // Préparer les paramètres
                const params = {
                    page: currentPage.value,
                    per_page: perPage.value,
                    sort_by: 'created_at',
                    sort_direction: 'desc'
                };

                // Ajouter les filtres
                if (search.value) {
                    params.search = search.value;
                }
                if (filters.role) {
                    params.role = filters.role;
                }
                if (filters.active !== '') {
                    params.active = filters.active === 'true';
                }

                // Appel à l'API
                const response = await usersService.getUsers(params);
                users.value = response.data.data || [];

                // Mettre à jour les données de pagination depuis meta
                if (response.data.meta) {
                    const meta = response.data.meta;
                    total.value = meta.total || 0;
                    perPage.value = meta.per_page || 10;
                    paginationFrom.value = meta.from || 0;
                    paginationTo.value = meta.to || 0;
                    // Calculer lastPage à partir de total et per_page
                    lastPage.value = Math.ceil(total.value / perPage.value);
                }

            } catch (error) {
                console.error('Erreur lors du chargement des utilisateurs:', error);
            } finally {
                loading.value = false;
            }
        };

        const handleSearch = () => {
            waitSearch.value = setTimeout(() => {
                currentPage.value = 1;
                loadUsers();
            }, 500);    
        };

        const goToPage = (page) => {
            if (page < 1 || page > lastPage.value) return;
            currentPage.value = page;
            loadUsers();
        };

        const showModal = (type, user = null) => {
            currentUser.value = user;

            if (type === 'add' || type === 'edit') {
                isEditMode.value = type === 'edit';

                if (isEditMode.value && user) {
                    userForm.id = user.id;
                    userForm.name = user.name;
                    userForm.email = user.email;
                    userForm.role = user.role;
                    userForm.active = user.active;
                } else {
                    // Réinitialiser le formulaire
                    userForm.id = null;
                    userForm.name = '';
                    userForm.email = '';
                    userForm.role = 'user';
                    userForm.active = true;
                }

                showUserFormModal.value = true;
            } else if (type === 'delete' && user) {
                showConfirmModal.value = true;
                confirmTitle.value = 'Supprimer l\'utilisateur';
                confirmMessage.value = `Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.name} ?`;
                confirmButtonText.value = 'Supprimer';
                confirmButtonClass.value = 'delete-confirm-btn';
                confirmAction.value = deleteUser;
            } else if (type === 'status' && user) {
                showConfirmModal.value = true;
                const action = user.active ? 'désactiver' : 'activer';
                confirmTitle.value = user.active ? 'Désactiver l\'utilisateur' : 'Activer l\'utilisateur';
                confirmMessage.value = `Êtes-vous sûr de vouloir ${action} l'utilisateur ${user.name} ?`;
                confirmButtonText.value = user.active ? 'Désactiver' : 'Activer';
                confirmButtonClass.value = user.active ? 'deactivate-confirm-btn' : 'activate-confirm-btn';
                confirmAction.value = toggleUserStatus;
            }
        };

        const closeModal = (type) => {
            if (type === 'form') {
                showUserFormModal.value = false;
            } else if (type === 'confirm') {
                showConfirmModal.value = false;
            }
        };

        const saveUser = async () => {
            try {
                saving.value = true;

                if (isEditMode.value) {
                    // Mise à jour d'un utilisateur existant
                    await usersService.updateUser(userForm.id, {
                        name: userForm.name,
                        email: userForm.email,
                        role: userForm.role,
                        active: userForm.active
                    });
                } else {
                    // Création d'un nouvel utilisateur
                    await usersService.createUser({
                        name: userForm.name,
                        email: userForm.email,
                        role: userForm.role
                    });
                }

                closeModal('form');
                loadUsers();
            } catch (error) {
                console.error('Erreur lors de la sauvegarde de l\'utilisateur:', error);
                // Gestion des erreurs (à implémenter selon les besoins)
            } finally {
                saving.value = false;
            }
        };

        const deleteUser = async () => {
            if (!currentUser.value) return;

            try {
                saving.value = true;
                await usersService.deleteUser(currentUser.value.id);
                closeModal('confirm');
                loadUsers();
            } catch (error) {
                console.error('Erreur lors de la suppression de l\'utilisateur:', error);
                // Gestion des erreurs (à implémenter selon les besoins)
            } finally {
                saving.value = false;
            }
        };

        const toggleUserStatus = async () => {
            if (!currentUser.value) return;

            try {
                saving.value = true;
                await usersService.toggleUserStatus(currentUser.value.id, !currentUser.value.active);
                closeModal('confirm');
                loadUsers();
            } catch (error) {
                console.error('Erreur lors du changement de statut de l\'utilisateur:', error);
                // Gestion des erreurs (à implémenter selon les besoins)
            } finally {
                saving.value = false;
            }
        };

        const getInitials = (name) => {
            if (!name) return '';
            return name
                .split(' ')
                .map(part => part.charAt(0))
                .join('')
                .toUpperCase()
                .substring(0, 2);
        };

        const formatDate = (dateString) => {
            if (!dateString) return '';

            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        };

        const formatPrice = (amount) => {
            if (amount === undefined || amount === null) return '0 XOF';

            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
            }).format(amount);
        };

        // Charger les données au montage du composant
        onMounted(() => {
            loadUsers();
        });

        return {
            users,
            loading,
            saving,
            search,
            filters,
            currentPage,
            lastPage,
            perPage,
            total,
            paginationFrom,
            paginationTo,
            userForm,
            isEditMode,
            showUserFormModal,
            showConfirmModal,
            confirmTitle,
            confirmMessage,
            confirmButtonText,
            confirmButtonClass,
            loadUsers,
            handleSearch,
            goToPage,
            showModal,
            closeModal,
            saveUser,
            confirmAction,
            getInitials,
            formatDate,
            formatPrice
        };
    }
});
</script>

<style scoped>
.users-list-view {
    padding: 0 0 40px 0;
}

/* En-tête */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.page-heading {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

.add-user-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background-color: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    font-size: 15px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.add-user-btn:hover {
    background-color: #094854;
}

/* Filtres */
.filters-container {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
    background-color: white;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.search-bar {
    flex: 1;
    min-width: 250px;
}

.input-with-icon {
    position: relative;
    width: 100%;
}

.input-with-icon svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
}

.input-with-icon input {
    width: 100%;
    padding: 12px 12px 12px 40px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    background-color: #f9fafb;
    transition: all 0.2s ease;
}

.input-with-icon input:focus {
    outline: none;
    border-color: #0A5C6B;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.filter-selects {
    display: flex;
    gap: 12px;
}

.filter-select select {
    padding: 10px 32px 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #f9fafb;
    font-size: 14px;
    min-width: 180px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-position: right 12px center;
    background-repeat: no-repeat;
    cursor: pointer;
}

.filter-select select:focus {
    outline: none;
    border-color: #0A5C6B;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

/* Tableau des utilisateurs */
.users-table-container {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    margin-bottom: 30px;
    overflow: hidden;
}

.users-table {
    width: 100%;
    border-collapse: collapse;
}

.users-table th {
    text-align: left;
    padding: 14px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.users-table td {
    padding: 14px 16px;
    font-size: 14px;
    color: #1e293b;
    border-bottom: 1px solid #f1f5f9;
}

.users-table tr:last-child td {
    border-bottom: none;
}

.users-table tr.inactive td {
    color: #94a3b8;
    background-color: #f8fafc;
}

.avatar-name {
    display: flex;
    align-items: center;
    gap: 12px;
}

.avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: #0A5C6B;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
}

.role-badge,
.status-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.role-badge {
    background-color: #f1f5f9;
    color: #475569;
}

.role-badge.admin {
    background-color: #e0f2fe;
    color: #0369a1;
}

.status-badge {
    background-color: #f1f5f9;
    color: #475569;
}

.status-badge.active {
    background-color: #ecfdf5;
    color: #059669;
}

.status-badge.inactive {
    background-color: #fee2e2;
    color: #dc2626;
}

.actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.edit-btn,
.status-btn,
.delete-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
}

.edit-btn {
    background-color: #eff6ff;
    color: #3b82f6;
}

.edit-btn:hover {
    background-color: #dbeafe;
}

.status-btn.activate {
    background-color: #ecfdf5;
    color: #059669;
}

.status-btn.activate:hover {
    background-color: #d1fae5;
}

.status-btn.deactivate {
    background-color: #fef9c3;
    color: #a16207;
}

.status-btn.deactivate:hover {
    background-color: #fef08a;
}

.delete-btn {
    background-color: #fee2e2;
    color: #dc2626;
}

.delete-btn:hover:not(:disabled) {
    background-color: #fecaca;
}

.delete-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f3f4f6;
    color: #9ca3af;
}

/* États vides et chargement */
.loading-container,
.no-users {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #0A5C6B;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

.spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.empty-state-icon {
    margin-bottom: 16px;
    color: #94a3b8;
}

.no-users h3 {
    color: #1e293b;
    font-size: 18px;
    margin-bottom: 8px;
}

.no-users p {
    color: #64748b;
    max-width: 400px;
    margin-bottom: 20px;
}

.start-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    background-color: #0A5C6B;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.start-btn:hover {
    background-color: #094854;
}


/* Modals */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    width: 100%;
    max-width: 500px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    color: #1e293b;
}

.close-btn {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
}

.close-btn:hover {
    background-color: #f1f5f9;
    color: #1e293b;
}

.modal-body {
    padding: 24px;
}

.form-group {
    margin-bottom: 16px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #475569;
}

.form-group input,
.form-group select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    background-color: #f9fafb;
    transition: all 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
    outline: none;
    border-color: #0A5C6B;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.toggle-switch {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
}

.toggle-switch input[type="checkbox"] {
    appearance: none;
    width: 44px;
    height: 24px;
    background-color: #e5e7eb;
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
}

.toggle-switch input[type="checkbox"]::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    top: 2px;
    left: 2px;
    transition: all 0.3s ease;
}

.toggle-switch input[type="checkbox"]:checked {
    background-color: #0A5C6B;
}

.toggle-switch input[type="checkbox"]:checked::before {
    left: 22px;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
}

.save-btn,
.cancel-btn,
.delete-confirm-btn,
.activate-confirm-btn,
.deactivate-confirm-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 100px;
}

.cancel-btn {
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.cancel-btn:hover {
    background-color: #e2e8f0;
}

.save-btn {
    background-color: #0A5C6B;
    color: white;
    border: none;
}

.save-btn:hover:not(:disabled) {
    background-color: #094854;
}

.save-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.delete-confirm-btn {
    background-color: #ef4444;
    color: white;
    border: none;
}

.delete-confirm-btn:hover:not(:disabled) {
    background-color: #dc2626;
}

.activate-confirm-btn {
    background-color: #10b981;
    color: white;
    border: none;
}

.activate-confirm-btn:hover:not(:disabled) {
    background-color: #059669;
}

.deactivate-confirm-btn {
    background-color: #f59e0b;
    color: white;
    border: none;
}

.deactivate-confirm-btn:hover:not(:disabled) {
    background-color: #d97706;
}

/* Responsive */
@media (max-width: 1024px) {
    .users-table {
        display: block;
        overflow-x: auto;
    }
}

@media (max-width: 768px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }

    .add-user-btn {
        width: 100%;
        justify-content: center;
    }

    .filters-container {
        flex-direction: column;
    }

    .filter-selects {
        width: 100%;
        flex-direction: column;
    }

    .filter-select {
        width: 100%;
    }

    .filter-select select {
        width: 100%;
    }

    .pagination {
        flex-direction: column;
        gap: 16px;
    }

    .page-numbers {
        display: none;
    }

    .actions {
        flex-direction: column;
        align-items: stretch;
    }

    .modal-content {
        width: 90%;
        max-height: 90vh;
        overflow-y: auto;
    }
}
</style>